<template>
    <ol
        class="main-menu-list"
        data-qa="main-menu-list"
    >
        <template
            v-for="(menuItem, menuIndex) in mainMenu"
            :key="menuIndex"
        >
            <li
                v-if="!menuItem.hideOnDesktop"
                ref="menuItems"
                class="main-menu-item"
            >
                <HeaderLink
                    v-qa="'main-menu-item-link'"
                    v-qa.index="menuIndex"
                    aria-haspopup="true"
                    :link="menuItem.link"
                    class="main-menu-item-link"
                >
                    {{ menuItem.title }}
                </HeaderLink>

                <div
                    v-qa.sub.menu="menuIndex"
                    :class="getClassSubMenu(menuItem.subMenu)"
                    aria-label="submenu"
                    class="sub-menu"
                >
                    <div class="sub-menu-container">
                        <div
                            v-if="
                                menuItem.subMenu &&
                                menuItem.subMenu.title &&
                                menuItem.subMenu.description
                            "
                            class="sub-menu-description"
                        >
                            <p class="menu-title">
                                {{ menuItem.subMenu.title }}
                            </p>

                            <p class="menu-description">
                                {{ menuItem.subMenu.description }}
                            </p>
                        </div>

                        <div
                            v-if="
                                menuItem.subMenu &&
                                menuItem.subMenu.sections.length
                            "
                            class="sub-menu-sections"
                            :class="{
                                'three-columns':
                                    menuItem.subMenu.editorialContent,
                            }"
                        >
                            <SubMenuSection
                                v-for="(section, sectionIndex) in menuItem
                                    .subMenu.sections"
                                :key="sectionIndex"
                                :section="section"
                                :use-large-format-items="
                                    menuItem.subMenu.useLargeFormatItems
                                "
                            />
                        </div>

                        <div
                            v-if="
                                menuItem.subMenu &&
                                menuItem.subMenu.editorialContent
                            "
                            class="sub-menu-article"
                        >
                            <HeaderLink
                                :link="menuItem.subMenu.editorialContent.link"
                                class="sub-menu-article-link"
                            >
                                <div class="sub-menu-article-image">
                                    <CmsImage
                                        :data="
                                            menuItem.subMenu.editorialContent
                                                .image
                                        "
                                        fit="cover"
                                    />
                                </div>

                                <span>
                                    {{ menuItem.subMenu.editorialContent.text }}
                                </span>
                            </HeaderLink>
                        </div>
                    </div>
                </div>
            </li>
        </template>
    </ol>
</template>

<script setup lang="ts">
import type { HeaderMenuFragment, SubMenuFragment } from '@/graphql/api'
import { hasImageInEachItem } from './helpers'

import HeaderLink from '@/components/layout/header/menu/HeaderLink.vue'
import CmsImage from '@/components/cms/CmsImage.vue'
import SubMenuSection from './SubMenuSection.vue'

defineProps<{
    mainMenu: HeaderMenuFragment[]
}>()

const getClassSubMenu = (subMenu: SubMenuFragment | null) => ({
    'with-images-only': subMenu ? hasImageInEachItem(subMenu) : false,
})
</script>

<style lang="scss" scoped>
.main-menu-list {
    color: $slate;
    z-index: 100;
    align-content: center;
    justify-content: center;
    display: flex;

    :deep(.main-menu-item) {
        &:hover {
            .main-menu-item-link {
                border-bottom: 2px solid $green;
            }

            .sub-menu {
                display: flex;
            }
        }

        .main-menu-item-link {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            color: $slate;
            font-family: $effra-font-stack;
            letter-spacing: 0.05em;
            font-weight: 400;
            text-transform: uppercase;
            font-size: 0.875rem;
            line-height: 1.25rem;
            padding: 32px 10px 26px;
            border-bottom: 2px solid transparent;

            @include min-width(1140) {
                padding: 30px 15px 28px;
            }
        }

        .sub-menu {
            padding: 0 30px 10px;
            font-family: $effra-font-stack;
            font-weight: 400;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            display: none;

            @include min-width(1024) {
                width: 100%;
                max-width: 1140px;
                height: 500px;
                padding: 30px;
                position: absolute;
                top: 80px;
                left: 0;
                right: 0;
                margin: 0 auto;
                background: $stone-3;
                border: 1px solid $grey-200;
                box-shadow: 0 5px 10px rgba($black, 0.1);
            }

            &.with-images-only {
                align-items: center;

                .sub-menu-container {
                    align-items: center;
                }

                .sub-menu-sections {
                    flex-flow: row;
                    align-items: center;

                    .sub-menu-section {
                        width: 50%;
                    }
                }
            }

            .sub-menu-container {
                width: 1024px;
                max-width: 100%;
                margin: 0 auto;
                display: flex;
                justify-content: center;
                align-items: flex-start;
            }

            .sub-menu-description {
                width: 360px;
                height: 100%;
                flex-shrink: 0;
                display: none;
                padding: 20px;
                flex-wrap: wrap;
                justify-content: flex-end;
                align-items: center;
                text-align: center;

                .menu-title {
                    width: 100%;
                    font-size: 1.5rem;
                    line-height: 1.2em;
                    font-family: $parcel-font-stack;
                    text-transform: initial;
                    letter-spacing: 0;
                    margin: 0 0 10px;
                    color: $blue;
                }

                .menu-description {
                    font-family: $sectra-font-stack;
                    font-size: 1rem;
                    line-height: 1.7rem;
                    text-transform: initial;
                    letter-spacing: 0;
                    height: 80px;
                    max-width: 300px;
                }

                @include min-width(1024) {
                    display: flex;
                }
            }

            .sub-menu-sections {
                width: 100%;
                height: 500px;
                margin: 0 auto;
                padding: 0 0 20px;
                display: flex;
                flex-flow: column wrap;

                &.three-columns {
                    .sub-menu-section {
                        @include min-width(1024) {
                            width: 33%;
                        }
                    }
                }
            }

            .sub-menu-article {
                width: 30%;
                flex-shrink: 0;
                display: none;
                height: 100%;

                @include min-width(1360) {
                    display: flex;
                    align-items: center;
                }

                .sub-menu-article-link {
                    width: 100%;
                    color: $slate;
                    text-align: center;

                    .sub-menu-article-image {
                        width: 220px;
                        height: 300px;
                        margin: 0 auto;
                        border-radius: 110px;
                        display: block;
                        background-repeat: no-repeat;
                        background-size: cover;
                        background-position: center center;
                        flex-shrink: 0;
                        overflow: hidden;
                        margin-bottom: 30px;
                    }

                    span {
                        font-size: 0.875rem;
                        width: 100%;
                        display: block;
                        position: relative;
                    }
                }
            }
        }
    }
}
</style>
